/* PoscART — storefront enhancements
 *
 * Loaded on every page from the "poscart_global" loader in
 * sections/header-group.json (defer). Two jobs, both scoped hard to the product
 * page (see the page-scope guard — playbook A4.2: product cards share markup
 * with the PDP, so nothing here may fall back to `document`):
 *
 *   1. Swatch groups. The theme renders the Colour picker as one flat wall of
 *      up to 60 radio+label pairs. Regroup them under Classic / Pastel /
 *      Metallic / Fluoro / Glitter headings using assets/poscart-colours.json.
 *      Inputs and labels are MOVED, never cloned, so the theme's variant-radios
 *      change listener (delegated on the wrapper) keeps working. If a colour is
 *      not in the JSON it goes in the first group — nothing is ever hidden.
 *      Also paints the swatch from the JSON when the theme has no colour for it.
 *
 *   2. "Your line" preview. An SVG stroke under the picker in the selected
 *      colour at the pen's tip width. Width is derived from the tip code found
 *      in the title/SKU (PC-5M → 2.5mm etc.); unknown → 2mm.
 *
 * Both re-run on variant change (the theme rewrites .selected_option_name and
 * the checked radio; a debounced MutationObserver on the picker catches it).
 *
 * WHY a JSON asset and not the theme's custom_colors setting alone: the JSON
 * carries finish + aliases, and the colour wall / hero read the same file, so
 * one edit fixes every surface. custom_colors is ALSO populated (settings_data)
 * so swatches are right with JS disabled.
 */
(function () {
  "use strict";
  if (window.__poscartBrand) return;
  window.__poscartBrand = 1;

  var cfg = document.getElementById("pa-config");
  var COLOURS_URL = cfg ? cfg.getAttribute("data-colours") : null;

  var TIPS = [ // code regex → [mm width for preview, human label]
    [/PCF[- ]?350/i, [2.2, "Brush tip"]],
    [/PC[- ]?17K/i,  [15, "Extra broad chisel · 15mm"]],
    [/PC[- ]?8K/i,   [8,  "Broad chisel · 8mm"]],
    [/PC[- ]?7M/i,   [5.5,"Bold bullet · 4.5–5.5mm"]],
    [/PC[- ]?5M/i,   [2.5,"Medium bullet · 1.8–2.5mm"]],
    [/PC[- ]?5BR/i,  [3,  "Bristle brush"]],
    [/PC[- ]?3ML/i,  [1.3,"Fine bullet · 0.9–1.3mm"]],
    [/PC[- ]?3M/i,   [1.3,"Fine bullet · 0.9–1.3mm"]],
    [/PC[- ]?1MR/i,  [0.7,"Ultra fine · 0.7mm"]],
    [/PC[- ]?1MC/i,  [0.9,"Extra fine · 0.7–1mm"]],
    [/PC[- ]?1M/i,   [0.9,"Extra fine · 0.7–1mm"]],
    [/PCM[- ]?22/i,  [4,  "Round · 3–4mm"]],
    [/PCM[- ]?23/i,  [6,  "Broad round"]]
  ];

  function norm(s) { return String(s || "").toLowerCase().replace(/\s*\|\s*[A-Za-z]{0,2}\d+\s*$/, "").replace(/posca/g, "").replace(/fluorescent/g, "fluoro").replace(/[^a-z0-9]+/g, ""); }

  /* page-scope guard: right page AND right section, or do nothing */
  function productRoot() {
    if (!/\/products\//.test(window.location.pathname)) return null;
    return document.querySelector('[id^="MainProduct-"], .product-section, section.product');
  }

  function tipInfo(root) {
    var txt = "";
    var t = root.querySelector(".product__title, h1"); if (t) txt += " " + t.textContent;
    var s = root.querySelector(".product__sku"); if (s) txt += " " + s.textContent;
    for (var i = 0; i < TIPS.length; i++) if (TIPS[i][0].test(txt)) return TIPS[i][1];
    return [2, ""];
  }

  function colourFieldset(root) {
    var fs = root.querySelector("fieldset.product-form__controls--colors");
    if (fs) return fs;
    var all = root.querySelectorAll("variant-radios fieldset");
    for (var i = 0; i < all.length; i++) { var lg = all[i].querySelector("legend"); if (lg && /colou?r/i.test(lg.textContent)) return all[i]; }
    return null;
  }

  function currentColour(fs) {
    var inp = fs.querySelector('input[type="radio"]:checked');
    return inp ? inp.value : "";
  }

  function build(root, data) {
    var fs = colourFieldset(root); if (!fs) return;
    var group = fs.querySelector(".product-form__controls-group"); if (!group) return;
    if (group.__paGrouped) return;
    group.__paGrouped = 1;

    var colours = data.colours || {}, finishes = data.finishes || [["classic","Classic"]];
    var pairs = [];
    var inputs = group.querySelectorAll('input[type="radio"]');
    inputs.forEach(function (inp) {
      var lab = inp.nextElementSibling;
      if (!lab || lab.tagName !== "LABEL") lab = group.querySelector('label[for="' + inp.id + '"]');
      if (!lab) return;
      var c = colours[norm(inp.value)];
      // paint the swatch if the theme fell back to the bare name
      var sw = lab.style.getPropertyValue("--swatch-color");
      if (c && c.h && (!sw || !/^(#|rgb|url|linear|var)/.test(sw.trim()))) lab.style.setProperty("--swatch-color", c.h);
      pairs.push({ inp: inp, lab: lab, finish: (c && c.f) || finishes[0][0], hex: c && c.h });
    });
    if (pairs.length < 8) return; // small ranges don't need grouping

    var buckets = {}, order = [];
    finishes.forEach(function (f) { buckets[f[0]] = []; order.push(f); });
    pairs.forEach(function (p) { (buckets[p.finish] || buckets[finishes[0][0]]).push(p); });

    var frag = document.createDocumentFragment();
    order.forEach(function (f) {
      var list = buckets[f[0]]; if (!list.length) return;
      var wrap = document.createElement("div"); wrap.className = "pa-swatch-group";
      var lbl = document.createElement("span"); lbl.className = "pa-swatch-group__label"; lbl.textContent = f[1] + " · " + list.length; wrap.appendChild(lbl);
      var row = document.createElement("div"); row.className = "pa-swatch-group__row";
      list.forEach(function (p) { row.appendChild(p.inp); row.appendChild(p.lab); });
      wrap.appendChild(row); frag.appendChild(wrap);
    });
    group.appendChild(frag);
    // line preview
    var tip = tipInfo(root);
    var prev = document.createElement("div"); prev.className = "pa-line-preview";
    var w = Math.max(1.5, Math.min(26, tip[0] * 2.4));
    prev.innerHTML = '<svg viewBox="0 0 400 26" preserveAspectRatio="none"><path class="pa-line-preview__stroke" d="M6 16 C 90 4, 250 26, 394 10" stroke="#ccc" stroke-width="' + w + '" stroke-linecap="round" fill="none"/></svg><small>Your line' + (tip[1] ? " · " + tip[1] : "") + '</small>';
    fs.appendChild(prev);
    var stroke = prev.querySelector("path");
    function paint() {
      var v = currentColour(fs); var c = colours[norm(v)];
      stroke.setAttribute("stroke", (c && c.h) || "#0A172C");
      stroke.style.filter = (c && c.f === "fluoro") ? "drop-shadow(0 0 4px " + c.h + ")" : "";
    }
    paint();
    fs.addEventListener("change", paint);
    var t; new MutationObserver(function () { clearTimeout(t); t = setTimeout(paint, 120); }).observe(fs, { subtree: true, attributes: true, attributeFilter: ["checked", "class"] });
  }

  function start() {
    var root = productRoot(); if (!root || !COLOURS_URL) return;
    fetch(COLOURS_URL, { credentials: "omit" }).then(function (r) { return r.json(); }).then(function (d) {
      build(root, d);
      // the theme can re-render the section on variant URL change; rebuild if the picker is replaced
      var t; new MutationObserver(function () { clearTimeout(t); t = setTimeout(function () { build(root, d); }, 200); }).observe(root, { childList: true, subtree: true });
    }).catch(function () {});
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start); else start();
})();
